/**
 * The last N COMPLETE calendar quarters, newest first — for the report Period picker
 * (kills the free-text placeholder footgun where a blank field silently defaulted to
 * "last quarter"). A quarter is COMPLETE once its end date has passed, so the quarter
 * CONTAINING `now` is never offered (it's still in progress), and neither is any future
 * quarter. Computed from `now` — never hardcode the current calendar year: in early
 * January the most-recent complete quarter is Q4 of the PREVIOUS year.
 *
 * Each entry pairs the real backend period `value` (e.g. "2026-Q3") with a human `label`
 * that includes the covered months (e.g. "Q3 2026 · Jul–Sep").
 *
 * Floored at `minValue` (default "2026-Q1" — Alchmy took its first client in 2026, so no
 * report can ever cover an earlier period). Near a year-start a fixed `count` alone would
 * spill into a year we never operated in; the floor caps the walk-back there. The list is
 * therefore [most-recent-complete … minValue], truncated to at most `count`.
 */
const QUARTER_MONTHS = ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'] as const;

// "YYYY-QN" → its quarter serial (year*4 + 0-based quarter index).
function serialOf(period: string): number {
  const [yStr, qStr] = period.split('-Q');
  return Number(yStr) * 4 + (Number(qStr) - 1);
}

export function recentCompleteQuarters(now: Date, count = 8, minValue = '2026-Q1'): { value: string; label: string }[] {
  // A "quarter serial" = year*4 + quarterIndex (0-based). The quarter containing `now`
  // is in progress, so the most-recent COMPLETE quarter is the one immediately before it.
  const currentSerial = now.getFullYear() * 4 + Math.floor(now.getMonth() / 3);
  const floorSerial = serialOf(minValue);
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const serial = currentSerial - 1 - i;
    if (serial < floorSerial) break; // never offer a quarter before minValue
    const year = Math.floor(serial / 4);
    const qIdx = ((serial % 4) + 4) % 4; // 0..3, safe if serial ever went negative
    const qNum = qIdx + 1;
    out.push({ value: `${year}-Q${qNum}`, label: `Q${qNum} ${year} · ${QUARTER_MONTHS[qIdx]}` });
  }
  return out;
}
